import React, { useState, useEffect } from 'react';
import CustomDropdown, { DropdownOption } from 'component/CustomDropdown';
import { ChartWidget, MetricKeySelection } from './dashboardSerializer';
import {
  DashboardDimensionItem,
  DashboardMetricItem,
} from 'domain/payload/response/clientTransmora/projectLayout/ProjectDashboardConfigResp';

interface DashboardWidgetConfigModalProps {
  show: boolean;
  widget: ChartWidget | null;
  dimensions: DashboardDimensionItem[];
  metrics: DashboardMetricItem[];
  onSave: (updatedWidget: ChartWidget) => void;
  onClose: () => void;
}

const chartTypeOptions: DropdownOption[] = [
  { id: 'bar', name: 'Bar Chart' },
  { id: 'pie', name: 'Pie Chart' },
  { id: 'heatmap', name: 'Heatmap / Matrix Chart' },
  { id: 'line', name: 'Line Chart' },
  { id: 'doughnut', name: 'Doughnut Chart' },
  { id: 'card', name: 'Stat Card' },
];

const DashboardWidgetConfigModal: React.FC<DashboardWidgetConfigModalProps> = ({
  show,
  widget,
  dimensions,
  metrics,
  onSave,
  onClose,
}) => {
  const [chartName, setChartName] = useState('');
  const [chartType, setChartType] = useState<string>('bar');
  const [selectedDimensionId, setSelectedDimensionId] = useState<string>('');
  const [metricSelections, setMetricSelections] = useState<MetricKeySelection[]>([]);

  useEffect(() => {
    if (widget) {
      setChartName(widget.chartName || '');
      setChartType(widget.chartType || 'bar');

      if (widget.dimensionKey && widget.dimensionKey.length > 0) {
        setSelectedDimensionId(widget.dimensionKey[0]);
      } else {
        setSelectedDimensionId('');
      }

      if (widget.metricKey && widget.metricKey.length > 0) {
        // Map and reconcile metric selections
        setMetricSelections(
          widget.metricKey.map((mk, idx) => {
            let matchedMetricId = mk.metricId || '';
            // If metricId doesn't match any API metric directly, try matching by index
            if (matchedMetricId && !metrics.some((m) => String(m.id) === String(matchedMetricId))) {
              if (metrics[idx]) {
                matchedMetricId = metrics[idx].id;
              }
            }

            return {
              metricId: matchedMetricId,
              metricDataId: Array.isArray(mk.metricDataId) ? [...mk.metricDataId] : [],
            };
          })
        );
      } else {
        setMetricSelections([]);
      }
    }
  }, [widget, metrics]);

  if (!show || !widget) return null;

  // Metric Selection Handlers
  const handleAddMetricEntry = () => {
    const unselected = metrics.find(
      (m) => !metricSelections.some((ms) => String(ms.metricId) === String(m.id))
    );
    const initialId = unselected ? unselected.id : metrics.length > 0 ? metrics[0].id : '';
    const initialDataIds = unselected && unselected.metricData ? unselected.metricData.map((d) => String(d.id)) : [];

    setMetricSelections([
      ...metricSelections,
      { metricId: initialId, metricDataId: initialDataIds },
    ]);
  };

  const handleRemoveMetricEntry = (index: number) => {
    setMetricSelections(metricSelections.filter((_, idx) => idx !== index));
  };

  const handleMetricIdChange = (index: number, newMetricId: string) => {
    const foundMet = metrics.find((m) => String(m.id) === String(newMetricId));
    const defaultDataIds = foundMet && foundMet.metricData ? foundMet.metricData.map((d) => String(d.id)) : [];

    setMetricSelections(
      metricSelections.map((ms, idx) => {
        if (idx === index) {
          return {
            metricId: newMetricId,
            metricDataId: defaultDataIds,
          };
        }
        return ms;
      })
    );
  };

  const handleToggleMetricDataId = (index: number, dataId: string) => {
    setMetricSelections(
      metricSelections.map((ms, idx) => {
        if (idx === index) {
          const strDataId = String(dataId);
          const currentMetObj = metrics.find((m) => String(m.id) === String(ms.metricId));
          const currentSelectedIds = getCheckedMetricDataIds(ms, currentMetObj);

          const exists = currentSelectedIds.some((id) => String(id) === strDataId);
          const nextDataIds = exists
            ? currentSelectedIds.filter((id) => String(id) !== strDataId)
            : [...currentSelectedIds, strDataId];
          return {
            ...ms,
            metricDataId: nextDataIds,
          };
        }
        return ms;
      })
    );
  };

  const handleToggleSelectAllMetricData = (index: number, metricObj: DashboardMetricItem) => {
    if (!metricObj || !metricObj.metricData) return;
    const allIds = metricObj.metricData.map((d) => String(d.id));
    const currentMs = metricSelections[index];
    const checkedIds = getCheckedMetricDataIds(currentMs, metricObj);
    const isAllSelected = allIds.every((id) => checkedIds.some((mId) => String(mId) === id));

    setMetricSelections(
      metricSelections.map((ms, idx) => {
        if (idx === index) {
          return {
            ...ms,
            metricDataId: isAllSelected ? [] : allIds,
          };
        }
        return ms;
      })
    );
  };

  // Helper to determine checked API metricData IDs for a metric selection
  const getCheckedMetricDataIds = (
    ms: MetricKeySelection,
    metricObj?: DashboardMetricItem
  ): string[] => {
    if (!metricObj || !metricObj.metricData || metricObj.metricData.length === 0) {
      return ms.metricDataId;
    }

    const realApiIds = metricObj.metricData.map((md) => String(md.id));

    // Exact matches
    const exactMatches = ms.metricDataId.filter((id) => realApiIds.includes(String(id)));
    if (exactMatches.length > 0) {
      return exactMatches;
    }

    // Fallback: If ms.metricDataId contains items (e.g. dummy UUIDs from sample JSON), map by index!
    if (ms.metricDataId.length > 0) {
      const indexedMatches: string[] = [];
      ms.metricDataId.forEach((_, idx) => {
        if (metricObj.metricData[idx]) {
          indexedMatches.push(String(metricObj.metricData[idx].id));
        }
      });
      if (indexedMatches.length > 0) return indexedMatches;
    }

    return realApiIds;
  };

  const handleSave = () => {

    const updated: ChartWidget = {
      ...widget,
      chartName: chartName.trim() || 'Untitled Chart',
      chartType,
    };

    if (chartType === 'heatmap') {
      const validMetrics = metricSelections.map((ms) => {
        const currentMetObj = metrics.find((m) => String(m.id) === String(ms.metricId));
        const checkedIds = getCheckedMetricDataIds(ms, currentMetObj);
        return {
          metricId: ms.metricId,
          metricDataId: checkedIds,
        };
      }).filter((ms) => ms.metricId);

      updated.metricKey = validMetrics.length > 0 ? validMetrics : undefined;
      updated.dimensionKey = undefined;
    } else {
      if (selectedDimensionId) {
        updated.dimensionKey = [selectedDimensionId];
      } else {
        updated.dimensionKey = undefined;
      }
      updated.metricKey = undefined;
    }

    onSave(updated);
  };

  // Build Dimension Options for CustomDropdown
  const dimensionOptions: DropdownOption[] = [
    { id: '', name: '-- None (Global) --' },
    ...dimensions.map((dim) => ({
      id: dim.id,
      name: `${dim.name} (${dim.code})`,
    })),
  ];

  return (
    <div
      className="modal fade show d-block"
      tabIndex={-1}
      style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }}
    >
      <div className="modal-dialog modal-dialog-centered modal-lg">
        <div className="modal-content shadow-lg rounded-3 border-0" style={{ overflow: 'visible' }}>
          <div className="modal-header bg-light border-bottom p-3">
            <h6 className="modal-title fw-bold text-dark d-flex align-items-center">
              <i className="fas fa-sliders-h text-primary me-2"></i>
              Configure Chart Widget
            </h6>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
            ></button>
          </div>

          <div>
            <div
              className="modal-body p-4 position-relative"
              style={{ overflow: 'visible' }}
            >
              {/* CHART NAME */}
              <div className="mb-3">
                <label className="form-label fw-semibold small text-dark">
                  Chart Name / Title <span className="text-danger">*</span>
                </label>
                <input
                  type="text"
                  className="form-control form-control-sm"
                  value={chartName}
                  placeholder="e.g. By Area, Coverage Availability..."
                  onChange={(e) => setChartName(e.target.value)}
                  required
                />
              </div>

              {/* CHART TYPE (CustomDropdown) */}
              <div className="mb-3">
                <label className="form-label fw-semibold small text-dark">Chart Type</label>
                <CustomDropdown
                  value={chartType}
                  options={chartTypeOptions}
                  onChange={(opt) => setChartType(opt.id ? String(opt.id) : 'bar')}
                  placeholder="Select Chart Type"
                  containerClassName="w-100"
                  buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                />
              </div>

              {/* DIMENSION KEY SELECTION (CustomDropdown floating over footer cleanly) */}
              {chartType !== 'heatmap' && (
                <div className="mb-3">
                  <label className="form-label fw-semibold small text-dark">
                    Dimension / Project Key
                  </label>
                  <CustomDropdown
                    value={selectedDimensionId}
                    options={dimensionOptions}
                    onChange={(opt) => setSelectedDimensionId(opt.id ? String(opt.id) : '')}
                    placeholder="-- None (Global) --"
                    searchable={dimensions.length > 5}
                    searchPlaceholder="Search dimension..."
                    containerClassName="w-100"
                    buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                  />
                  <div className="form-text small text-muted" style={{ fontSize: '11px' }}>
                    Select a project key dimension to group and break down this chart.
                  </div>
                </div>
              )}

              {/* MULTI-METRIC SELECTION (FOR HEATMAP) */}
              {chartType === 'heatmap' && (
                <div className="mb-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label fw-semibold small text-dark mb-0">
                      Metric Keys ({metricSelections.length} Metrics Configured)
                    </label>
                    <button
                      type="button"
                      className="tb-btn tb-btn-primary px-2 py-1"
                      style={{ fontSize: '11px' }}
                      onClick={handleAddMetricEntry}
                    >
                      <i className="fas fa-plus me-1"></i> Add Metric
                    </button>
                  </div>

                  {metricSelections.length === 0 ? (
                    <div className="p-3 border rounded text-center text-muted bg-light small">
                      No metrics added yet. Click "+ Add Metric" above to configure metrics for this heatmap.
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3">
                      {metricSelections.map((ms, msIdx) => {
                        const currentMetObj = metrics.find(
                          (m) => String(m.id) === String(ms.metricId)
                        );
                        const checkedIds = getCheckedMetricDataIds(ms, currentMetObj);

                        // Build Metric Options for CustomDropdown
                        const metricOptions: DropdownOption[] = metrics.map((met) => ({
                          id: met.id,
                          name: met.name,
                        }));

                        if (ms.metricId && !metricOptions.some((opt) => String(opt.id) === String(ms.metricId))) {
                          metricOptions.unshift({
                            id: ms.metricId,
                            name: `Metric (${ms.metricId})`,
                          });
                        }

                        return (
                          <div
                            key={msIdx}
                            className="p-3 border rounded-3 bg-light position-relative shadow-xs"
                            style={{ overflow: 'visible' }}
                          >
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <span className="badge bg-primary px-2 py-1" style={{ fontSize: '10px' }}>
                                Metric #{msIdx + 1}
                              </span>
                              <button
                                type="button"
                                className="tb-btn tb-btn-icon tb-btn-danger"
                                title="Remove Metric"
                                onClick={() => handleRemoveMetricEntry(msIdx)}
                              >
                                <i className="fas fa-trash-alt" style={{ fontSize: '11px' }}></i>
                              </button>
                            </div>

                            {/* Metric Selector (CustomDropdown) */}
                            <div className="mb-2">
                              <CustomDropdown
                                value={ms.metricId}
                                options={metricOptions}
                                onChange={(opt) => handleMetricIdChange(msIdx, opt.id ? String(opt.id) : '')}
                                placeholder="-- Select Metric --"
                                searchable={metricOptions.length > 5}
                                searchPlaceholder="Search metric..."
                                containerClassName="w-100"
                                buttonClassName="form-control form-control-compact w-100 d-flex align-items-center justify-content-between text-start bg-white text-dark border rounded-3"
                              />
                            </div>

                            {/* Sub-metric Items Checkboxes */}
                            {currentMetObj && currentMetObj.metricData && currentMetObj.metricData.length > 0 && (
                              <div>
                                <div className="d-flex justify-content-between align-items-center mb-1">
                                  <label
                                    className="form-label fw-semibold text-secondary mb-0"
                                    style={{ fontSize: '10px' }}
                                  >
                                    Metric Data Items ({checkedIds.length} selected):
                                  </label>
                                  <button
                                    type="button"
                                    className="btn btn-link p-0 text-decoration-none small"
                                    style={{ fontSize: '10px' }}
                                    onClick={() => handleToggleSelectAllMetricData(msIdx, currentMetObj)}
                                  >
                                    {checkedIds.length === currentMetObj.metricData.length
                                      ? 'Deselect All'
                                      : 'Select All'}
                                  </button>
                                </div>
                                <div className="border rounded p-2 bg-white" style={{ maxHeight: '140px', overflowY: 'auto' }}>
                                  {currentMetObj.metricData.map((md) => {
                                    const isChecked = checkedIds.some(
                                      (id) => String(id) === String(md.id)
                                    );
                                    return (
                                      <div className="form-check form-check-sm mb-1" key={md.id}>
                                        <input
                                          className="form-check-input cursor-pointer"
                                          type="checkbox"
                                          id={`md-${msIdx}-${md.id}`}
                                          checked={isChecked}
                                          onChange={() => handleToggleMetricDataId(msIdx, md.id)}
                                        />
                                        <label
                                          className="form-check-label small cursor-pointer"
                                          htmlFor={`md-${msIdx}-${md.id}`}
                                          style={{ fontSize: '11px' }}
                                        >
                                          {md.name}
                                        </label>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer bg-light border-top p-3 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-outline-primary px-4"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary px-4 ms-2"
                onClick={handleSave}
              >
                Save Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardWidgetConfigModal;
